import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Grid,
  TextField,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { updateTask, addMessage, getTaskMessages } from '../services/kanTaskService';
import TaskFormBase from './shared/TaskFormBase';
import { useAuth } from '../contexts/AuthContext';

const getMemberIdentifier = (member) => {
  if (!member) return '';
  if (typeof member === 'string') return member;
  if (member._id) return String(member._id);
  if (member.user && member.user._id) return String(member.user._id);
  return '';
};

const EditTaskModal = ({
  open,
  onClose,
  onTaskUpdate = () => {},
  task,
  teamMembers,
  castMembers,
}) => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    phase: 'pre-production',
    department: 'production',
    priority: 'normal',
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    location: '',
    progress: 0,
    isMilestone: false,
    assignees: [],
    castMembers: [],
    crewMembers: [], // Added crew members array
    messages: [],
    newMessage: '',
    attachments: [],
    image: null,
    checklist: [],
  });
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (task) {
      console.log('Loading task data for editing:', JSON.stringify(task));
      
      // Load messages for this task
      const loadMessages = async () => {
        try {
          if (task._id) {
            const messages = await getTaskMessages(task._id);
            console.log('Loaded messages for task:', messages);
            
            // Format messages for display
            const formattedMessages = messages.map(msg => ({
              ...msg,
              // Ensure sender is properly formatted
              senderName: msg.sender?.name || (typeof msg.sender === 'object' ? msg.sender.name : 'Unknown'),
              // Ensure timestamp is in the right format
              timestamp: msg.timestamp || new Date().toISOString()
            }));
            
            // Update form data with the loaded messages
            setFormData(prev => ({
              ...prev,
              messages: formattedMessages
            }));
          }
        } catch (error) {
          console.error('Error loading messages:', error);
        }
      };
      
      loadMessages();
      
      console.log('Team members available:', JSON.stringify(teamMembers));
      
      // Process crew members to ensure they're in the correct format for the form
      console.log('Processing crew members from task:', JSON.stringify(task.crewMembers));
      
      // Create a deep copy of the crew members to avoid reference issues
      const processedCrewMembers = [];
      
      // Process each crew member
      (task.crewMembers || []).forEach(member => {
        console.log('Processing crew member:', JSON.stringify(member));
        
        // Case 1: Member is already a full object with name
        if (member && member.name) {
          console.log('Using fully populated crew member with name:', member.name);
          processedCrewMembers.push({
            _id: member._id,
            name: member.name,
            email: member.email || '',
            role: member.role || '',
            department: member.department || ''
          });
          return;
        }
        
        // Case 2: Member has a nested user object with name
        if (member && member.user && member.user.name) {
          console.log('Using crew member with nested user object:', member.user.name);
          processedCrewMembers.push({
            _id: member.user._id,
            name: member.user.name,
            email: member.user.email || '',
            role: member.user.role || '',
            department: member.user.department || ''
          });
          return;
        }
        
        // Case 3: Member is just an ID (string or object)
        let memberId = null;
        if (typeof member === 'string') {
          memberId = member;
        } else if (member && member._id) {
          memberId = member._id;
        }
        
        if (!memberId) {
          console.log('Could not extract ID from crew member:', JSON.stringify(member));
          return;
        }
        
        console.log('Looking for team member with ID:', memberId);
        
        // Try to find the member in the team members list
        if (teamMembers && teamMembers.length > 0) {
          const foundMember = teamMembers.find(tm => {
            const tmId = tm._id || (tm.user && tm.user._id);
            return String(tmId) === String(memberId);
          });
          
          if (foundMember) {
            console.log('Found team member in list:', foundMember.name || 'Unknown');
            // Use the found team member
            if (foundMember.name) {
              processedCrewMembers.push(foundMember);
            } else if (foundMember.user && foundMember.user.name) {
              processedCrewMembers.push({
                _id: foundMember.user._id,
                name: foundMember.user.name,
                email: foundMember.user.email || '',
                role: foundMember.user.role || '',
                department: foundMember.user.department || ''
              });
            }
          } else {
            // If we can't find the member, create a placeholder
            console.log('Creating placeholder for crew member ID:', memberId);
            processedCrewMembers.push({ 
              _id: memberId,
              name: `User ${memberId.slice(-5)}`,
              email: 'user@example.com'
            });
          }
        } else {
          // If no team members list is available
          console.log('No team members list available, creating placeholder');
          processedCrewMembers.push({ 
            _id: memberId,
            name: `User ${memberId.slice(-5)}`,
            email: 'user@example.com'
          });
        }
      });
      
      console.log('Final processed crew members for form:', JSON.stringify(processedCrewMembers));
      
      console.log('Task image when loading for edit:', task.image);
      setFormData({
        ...formData,
        ...task,
        startDate: task.startDate ? new Date(task.startDate) : new Date(),
        endDate: task.endDate ? new Date(task.endDate) : new Date(new Date().setDate(new Date().getDate() + 7)),
        crewMembers: processedCrewMembers,
        image: task.image || null, // Explicitly preserve image
      });
    } else {
      setFormData({
        title: '',
        description: '',
        phase: 'pre-production',
        department: 'production',
        priority: 'normal',
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
        location: '',
        progress: 0,
        isMilestone: false,
        assignees: [],
        castMembers: [],
        crewMembers: [], // Added crew members array
        messages: [],
        newMessage: '',
        attachments: [],
        image: null,
      });
    }
  }, [task, teamMembers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...files]
      }));
    }
  };

  const handleMessageSubmit = async () => {
    if (!formData.newMessage?.trim()) return;

    // Check for user in different possible formats
    const userId = user?._id || (user?.user && user.user._id) || user?.id;
    const userName = user?.name || (user?.user && user.user.name) || user?.username || 'Anonymous';
    
    if (!userId) {
      console.error('Cannot add message: User not logged in');
      setError('You must be logged in to send messages');
      return;
    }

    try {
      setIsLoading(true);
      
      // Create a local message object for immediate UI update
      const newMessage = {
        content: formData.newMessage.trim(),
        sender: userId,
        senderName: userName,
        timestamp: new Date().toISOString(),
      };

      // Update local state for immediate feedback
      setFormData(prev => ({
        ...prev,
        messages: [...(prev.messages || []), newMessage],
        newMessage: '',
      }));
      
      // Send the message to the server
      if (task?._id) {
        try {
          // Use the new service method to add the message
          const messageResponse = await addMessage(
            task._id,
            newMessage.content,
            userId
          );
          
          console.log('Server response from addMessage:', messageResponse);
          
          // Fetch the updated messages to ensure we have the server-side data
          const updatedMessages = await getTaskMessages(task._id);
          console.log('Updated messages from server:', updatedMessages);
          
          // Make sure we have the messages in the right format
          const formattedMessages = updatedMessages.map(msg => ({
            ...msg,
            // Ensure sender is properly formatted
            senderName: msg.sender?.name || (typeof msg.sender === 'object' ? msg.sender.name : 'Unknown'),
            // Ensure timestamp is in the right format
            timestamp: msg.timestamp || new Date().toISOString()
          }));
          
          console.log('Formatted messages for UI:', formattedMessages);
          
          // Update the form data with the server-returned messages
          setFormData(prev => ({
            ...prev,
            messages: formattedMessages,
          }));
          
          console.log('Message added successfully');
        } catch (error) {
          console.error('Error in message handling:', error);
          setError('Error adding message: ' + error.message);
        }
      } else {
        console.warn('Task not saved yet, message will be saved with the task');
      }
    } catch (error) {
      console.error('Error adding message:', error);
      // Show error to user
      setError('Failed to add message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Checklist handlers
  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    
    const newItem = {
      text: newChecklistItem,
      completed: false,
      createdBy: user._id,
      createdAt: new Date()
    };
    
    setFormData(prev => ({
      ...prev,
      checklist: [...(prev.checklist || []), newItem]
    }));
    
    setNewChecklistItem('');
  };
  
  const handleToggleChecklistItem = (index) => {
    setFormData(prev => {
      const updatedChecklist = [...prev.checklist];
      updatedChecklist[index] = {
        ...updatedChecklist[index],
        completed: !updatedChecklist[index].completed,
        completedBy: !updatedChecklist[index].completed ? user._id : null,
        completedAt: !updatedChecklist[index].completed ? new Date() : null
      };
      return {
        ...prev,
        checklist: updatedChecklist
      };
    });
  };
  
  const handleRemoveChecklistItem = (index) => {
    setFormData(prev => {
      const updatedChecklist = prev.checklist.filter((_, i) => i !== index);
      return {
        ...prev,
        checklist: updatedChecklist
      };
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title?.trim()) {
        setError('Title is required');
        return;
      }

      if (!task?._id) {
        setError('No task selected for editing');
        return;
      }

      // Format messages to ensure they have proper sender info
      const validMessages = (formData.messages || [])
        .filter(msg => msg && msg.content && msg.sender)
        .map(msg => ({
          content: msg.content.trim(),
          sender: msg.sender,
          senderName: msg.senderName || 'Anonymous',
          timestamp: msg.timestamp || new Date().toISOString()
        }));

      // Format image data
      let imageData = formData.image;
      console.log('Processing image data:', imageData ? 'Image present' : 'No image');
      
      if (imageData instanceof File) {
        console.log('Image is a File object, converting to base64...');
        // Convert File to base64 string
        const reader = new FileReader();
        imageData = await new Promise((resolve) => {
          reader.onloadend = () => {
            console.log('File converted to base64 successfully');
            resolve(reader.result);
          };
          reader.onerror = (error) => {
            console.error('Error converting file to base64:', error);
            resolve(null); // Return null on error
          };
          reader.readAsDataURL(imageData);
        });
      } else if (typeof imageData === 'string' && imageData.trim() === '') {
        console.log('Empty string image data - will delete image on server');
        // Keep as empty string - backend interprets '' as "delete image"
      } else if (imageData === null || imageData === undefined) {
        console.log('Null or undefined image data');
        imageData = null;
      } else if (typeof imageData === 'string') {
        console.log('Image is already a string (URL or base64)');
        // It's already a string (URL or base64), keep as is
      }
      
      console.log('Final image data type:', typeof imageData);
      if (typeof imageData === 'string') {
        console.log('Image data length:', imageData.length, 'Preview:', imageData.substring(0, 50) + '...');
      }

      // Format dates properly - handle both Date objects and string dates
      const formatDate = (dateValue) => {
        if (!dateValue) return new Date().toISOString();
        if (dateValue instanceof Date) return dateValue.toISOString();
        // If it's a string from the date input (YYYY-MM-DD format)
        if (typeof dateValue === 'string') {
          // Create a new date object from the string
          const date = new Date(dateValue);
          return date.toISOString();
        }
        return new Date().toISOString(); // Fallback
      };

      const taskData = {
        ...formData,
        _id: task._id,
        title: formData.title.trim(),
        description: formData.description?.trim() || '',
        startDate: formatDate(formData.startDate),
        endDate: formatDate(formData.endDate),
        assignees: formData.assignees?.map(assignee => assignee._id) || [],
        castMembers: formData.castMembers?.map(member => member._id) || [],
        crewMembers: formData.crewMembers?.map(member => {
          // Handle different possible structures of crew member
          console.log('Processing crew member in EditTaskModal:', JSON.stringify(member));
          if (typeof member === 'string') {
            return member; // Already an ID
          } else if (member && member._id) {
            return member._id; // Extract ID from object
          } else if (member && member.user && member.user._id) {
            return member.user._id; // Extract ID from nested user object
          }
          return null; // Invalid member
        }).filter(Boolean) || [], // Added crew members and filter out null values
        messages: validMessages,
        attachments: formData.attachments || [],
        image: imageData,
        checklist: formData.checklist || [], // Include checklist data
        priority: formData.priority || 'normal', // Ensure priority is one of: 'low', 'normal', 'high'
        phase: formData.phase || 'pre-production',
        department: formData.department || 'production',
        status: formData.status || 'pending'
      };

      await onTaskUpdate(taskData);
      setError('');
      onClose();
    } catch (error) {
      console.error('Error updating task:', error);
      setError(error.message || 'Failed to update task. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backdropFilter: 'blur(10px)',
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.85),
          borderRadius: 3,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: (theme) => `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.1)}`,
          overflow: 'hidden',
          maxHeight: '90vh',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #2196F3, #1976D2)',
            opacity: 0.7
          }
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(90deg, #2196F3, #1976D2)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        textFillColor: 'transparent',
        fontWeight: 500,
        letterSpacing: '0.5px',
        '&::before': {
          content: '""',
          display: 'inline-block',
          width: '24px',
          height: '24px',
          marginRight: '8px',
          background: `url('data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#2196F3" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/></svg>')}')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          filter: 'drop-shadow(0 0 2px rgba(33, 150, 243, 0.4))'
        }
      }}>
        <span>Edit Task</span>
        <IconButton onClick={onClose} size="small" sx={{ WebkitTextFillColor: 'initial' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': { 
                minHeight: '48px',
                py: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
              },
              '& .MuiTab-iconWrapper': {
                mr: 1,
              }
            }}
          >
            <Tab icon={<DescriptionIcon />} label="Details" id="tab-0" aria-controls="tabpanel-0" />
            <Tab icon={<MessageIcon />} label="Messages" id="tab-1" aria-controls="tabpanel-1" />
            <Tab icon={<CheckCircleIcon />} label="Checklist" id="tab-2" aria-controls="tabpanel-2" />
            <Tab icon={<InfoIcon />} label="More Info" id="tab-3" aria-controls="tabpanel-3" />
          </Tabs>
        </Box>

        {/* Error message - shown on all tabs */}
        {error && (
          <Box sx={{
            mx: 2,
            mt: 2,
            p: 1,
            bgcolor: 'error.light',
            color: 'error.dark',
            borderRadius: 1
          }}>
            {error}
          </Box>
        )}

        {/* Tab 0: Details */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 0}
          id="tabpanel-0"
          aria-labelledby="tab-0"
          sx={{ p: 2 }}
        >
          {activeTab === 0 && (
            <Box component="form" onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    label="Title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={Boolean(error && !formData.title?.trim())}
                    helperText={error && !formData.title?.trim() ? 'Title is required' : ''}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label="Description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                {/* Image Upload Section - Compact Design */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    mb: 2, 
                    p: 1, 
                    border: '1px dashed #ccc', 
                    borderRadius: 1,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '60px'
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      id="task-image-upload"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFormData(prev => ({
                              ...prev,
                              image: file
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    
                    {formData.image ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '4px', 
                          overflow: 'hidden',
                          mr: 1,
                          flexShrink: 0
                        }}>
                          <img 
                            src={typeof formData.image === 'string' ? formData.image : URL.createObjectURL(formData.image)}
                            alt="Preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                        <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.8rem' }}>
                          {formData.image instanceof File ? formData.image.name : 'Image attached'}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <ImageIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          Add image
                        </Typography>
                        <label htmlFor="task-image-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            size="small"
                          >
                            Browse
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                {/* Date fields in a container to ensure they're side by side */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Date Range</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      name="startDate"
                      label="Start Date"
                      type="date"
                      value={formData.startDate instanceof Date ? formData.startDate.toISOString().split('T')[0] : formData.startDate || ''}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ 
                        mb: 2,
                        '& input::-webkit-calendar-picker-indicator': {
                          filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                        }
                      }}
                    />
                    <TextField
                      name="endDate"
                      label="End Date"
                      type="date"
                      value={formData.endDate instanceof Date ? formData.endDate.toISOString().split('T')[0] : formData.endDate || ''}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ 
                        mb: 2,
                        '& input::-webkit-calendar-picker-indicator': {
                          filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                        }
                      }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="priority"
                    label="Priority"
                    select
                    value={formData.priority || 'normal'}
                    onChange={handleChange}
                    fullWidth
                    SelectProps={{ native: true }}
                    sx={{ mb: 2 }}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </TextField>
                </Grid>
                

              </Grid>
            </Box>
          )}
        </Box>

        {/* Tab 1: Messages */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 1}
          id="tabpanel-1"
          aria-labelledby="tab-1"
          sx={{ p: 2 }}
        >
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <MessageIcon sx={{ mr: 1 }} /> Task Communication
              </Typography>
              
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                  p: 1,
                  borderRadius: 1,
                  mb: 2
                }}
              >
                {Array.isArray(formData.messages) && formData.messages.length > 0 ? (
                  formData.messages.map((message, index) => (
                    <Box
                      key={`message-${index}`}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {message.senderName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {message.content}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No messages yet. Start a conversation!
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Type a message..."
                  value={formData.newMessage || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, newMessage: e.target.value }))}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleMessageSubmit(formData.newMessage);
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleMessageSubmit(formData.newMessage)}
                  disabled={!formData.newMessage?.trim()}
                  startIcon={<MessageIcon />}
                >
                  Send
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Tab 2: Checklist */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 2}
          id="tabpanel-2"
          aria-labelledby="tab-2"
          sx={{ p: 2 }}
        >
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                fontWeight: 600,
                color: theme => theme.palette.primary.main 
              }}>
                <CheckCircleIcon sx={{ mr: 1 }} /> Task Checklist
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Add new checklist item"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChecklistItem();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={handleAddChecklistItem}
                        size="small"
                        color="primary"
                        disabled={!newChecklistItem.trim()}
                      >
                        <AddIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
              
              <List sx={{ 
                maxHeight: '400px', 
                overflow: 'auto',
                bgcolor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                borderRadius: 1,
                p: 1
              }}>
                {formData.checklist && formData.checklist.length > 0 ? (
                  formData.checklist.map((item, index) => (
                    <ListItem 
                      key={`checklist-${index}`}
                      dense
                      sx={{
                        mb: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1,
                        '&:hover': {
                          boxShadow: 2
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={item.completed}
                          onChange={() => handleToggleChecklistItem(index)}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckBoxIcon />}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          style: item.completed ? { textDecoration: 'line-through', color: '#888' } : {}
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleRemoveChecklistItem(index)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No checklist items yet. Add some tasks to track progress!
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          )}
        </Box>

        {/* Tab 3: More Info */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 3}
          id="tabpanel-3"
          aria-labelledby="tab-3"
          sx={{ p: 2 }}
        >
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1 }} /> Additional Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="location"
                    label="Location"
                    value={formData.location || ''}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="department"
                    label="Department"
                    select
                    value={formData.department || 'production'}
                    onChange={handleChange}
                    fullWidth
                    SelectProps={{ native: true }}
                    sx={{ mb: 2 }}
                  >
                    <option value="production">Production</option>
                    <option value="art">Art</option>
                    <option value="camera">Camera</option>
                    <option value="sound">Sound</option>
                    <option value="lighting">Lighting</option>
                    <option value="costume">Costume</option>
                    <option value="makeup">Makeup</option>
                    <option value="editing">Editing</option>
                    <option value="vfx">VFX</option>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phase"
                    label="Phase"
                    select
                    value={formData.phase || 'pre-production'}
                    onChange={handleChange}
                    fullWidth
                    SelectProps={{ native: true }}
                    sx={{ mb: 2 }}
                  >
                    <option value="pre-production">Pre-Production</option>
                    <option value="production">Production</option>
                    <option value="post-production">Post-Production</option>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isMilestone || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, isMilestone: e.target.checked }))}
                        name="isMilestone"
                        color="primary"
                      />
                    }
                    label="Mark as Milestone"
                  />
                </Grid>
                
                {/* Crew Members Assignment Section */}
                <Grid item xs={12}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="crew-members-label">Assign Crew Members</InputLabel>
                    <Select
                      labelId="crew-members-label"
                      id="crew-members-select"
                      multiple
                      value={formData.crewMembers || []}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          crewMembers: e.target.value
                        }));
                      }}
                      input={<OutlinedInput label="Assign Crew Members" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((member, idx) => {
                            const memberKey = getMemberIdentifier(member) || `member-${idx}`;
                            const memberLabel = member.name || (member.user && member.user.name) || 'Unknown';
                            return (
                              <Chip 
                                key={memberKey}
                                label={memberLabel}
                                size="small"
                                onDelete={(e) => {
                                  e.stopPropagation(); // Prevent dropdown from opening
                                  const targetId = getMemberIdentifier(member);
                                  const newCrewMembers = (formData.crewMembers || []).filter(
                                    (m) => getMemberIdentifier(m) !== targetId
                                  );
                                  setFormData(prev => ({
                                    ...prev,
                                    crewMembers: newCrewMembers
                                  }));
                                }}
                                onMouseDown={(e) => e.stopPropagation()} // Prevent dropdown from opening
                              />
                            );
                          })}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 250
                          },
                        },
                      }}
                    >
                      {teamMembers && teamMembers.length > 0 ? (
                        teamMembers.map((member, idx) => (
                          <MenuItem key={getMemberIdentifier(member) || `team-${idx}`} value={member}>
                            <Checkbox 
                              checked={formData.crewMembers?.some(m => 
                                (m._id === member._id) || (m.user && m.user._id === member._id)
                              )} 
                            />
                            <ListItemText 
                              primary={member.name || (member.user && member.user.name) || 'Unknown'}
                              secondary={member.role || member.department || 'Team Member'}
                            />
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No team members available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={onClose}
          sx={{ 
            borderRadius: 2,
            backdropFilter: 'blur(4px)',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
            color: (theme) => theme.palette.text.secondary,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
              boxShadow: (theme) => `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.08)}`
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          startIcon={<SaveIcon />}
          sx={{ 
            borderRadius: 2,
            background: 'linear-gradient(45deg, #2196F3 30%, #1976D2 90%)',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 4px 20px 0 rgba(33, 150, 243, 0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #2196F3 10%, #1976D2 70%)',
              boxShadow: '0 6px 25px 0 rgba(33, 150, 243, 0.4)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease-in-out'
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTaskModal;
